import { Box, Text, Center, Image, Flex } from "@chakra-ui/react";
import { useHooks } from "@/pages/updateKycInfo/hooks";
import { useI18n } from "@/hooks/useI18n";
import { useEffect, useMemo } from "react";
import BXNSelect from "@/components/ui/WSelect";
import { useAppSelector } from "@/state/hooks";
import { BXInput, BXButton } from "@/styles";
import useConstantsHooks from "@/pages/updateKycInfo/common/constantsHooks";
import BXLoading from "@/components/ui/pay-loading";
import ConfirmModal from "@/pages/updateKycInfo/common/ConfirmModal";
import BXUpload from "@/components/ui/BXUpload";
import Result from "@/pages/updateKycInfo/common/result";
import { isLaterThanNowByAtLeast3Months } from "@/utils/index";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import ZHlocale from "antd/es/date-picker/locale/zh_CN";
import PageWrap from "@/components/PageWrap";
import { getUserInfoApi } from "@/api/common";

const UpdateKycInfo = ({ riskLevel, reviewUUID }) => {
  const { t } = useI18n();
  const _lang = useAppSelector((state) => state.user.language);
  const {
    inputParams,
    inputOnChange,
    sanctionsCountryList,
    noCompliantCountryList,
    allCountryList,
    areaScanError,
    otherCountryAreaError,
    setOtherCountryAreaError,
    setAreaScanError,
    setAreaCompliantError,
    handleUploadOtherCountryIdCardFile,
    handleUoloadEsFile,
    uploadLoading,
    onSubmit,
    setShowTipModal,
    showTipModal,
    pageStatus,
    showOtherCountryBlock,
    needUpdateAddress,
    setNeedUpdateAddress,
    sumitPreCheck,
  } = useHooks({ riskLevel, reviewUUID });

  const { memoCardType, memoHasOtherCountryCard, memoOtherCountryCardType } =
    useConstantsHooks();
  const {
    id_type,
    has_other_country_id_card,
    other_id_type,
    id_country,
    other_id_country,
    id_copy_files,
    clone_address_files,
    other_id_opposite_files,
    other_id_front_files,
  } = inputParams;

  const getUserInfo = async () => {
    await getUserInfoApi();
  };

  const selectOtherCountry = (value) => {
    inputOnChange(value.iso_code2, "other_id_country");
    setOtherCountryAreaError(false);
    // 在制裁区
    if (sanctionsCountryList.indexOf(value.iso_code2) > -1) {
      setOtherCountryAreaError(true);
      return;
    }
    // 非合规区
    if (noCompliantCountryList.indexOf(value.iso_code2) > -1) {
      setOtherCountryAreaError(true);
      return;
    }
  };

  const memoCountryList = useMemo(() => {
    if (allCountryList && allCountryList.length > 0) {
      return allCountryList.map((d) => {
        return {
          ...d,
          label: d.zh_name,
          value: d.value,
        };
      });
    }
  }, [allCountryList]);

  //证件发行地国家
  const defaultCountry = useMemo(() => {
    if (memoCountryList?.length > 0) {
      if (id_country) {
        return memoCountryList.filter((d) => d.iso_code2 === id_country)[0];
      }
    }
  }, [id_country, memoCountryList]);

  // 其他证件发行地国家
  const defaultOtherCountry = useMemo(() => {
    if (memoCountryList?.length > 0) {
      if (other_id_country) {
        return memoCountryList.filter(
          (d) => d.iso_code2 === other_id_country,
        )[0];
      }
    }
  }, [other_id_country, memoCountryList]);

  // 设置 其他证件发行地国家证件
  const defaultOtherCountryCardType = useMemo(() => {
    if (memoOtherCountryCardType?.length > 0) {
      if (other_id_type) {
        return memoOtherCountryCardType.filter(
          (d) => d.value === other_id_type,
        )[0];
      }
    }
  }, [other_id_type, memoOtherCountryCardType]);

  // 常居地
  const memoResidenceCountry = useMemo(() => {
    if (allCountryList?.length > 0) {
      if (id_type === "ID_CARD") {
        const hk_opt = memoCountryList?.filter((d) => d.iso_code2 === "HK")[0];
        if (hk_opt) {
          if (_lang === "zh")
            return `${hk_opt?.zh_name} (+${hk_opt?.dial_code}) `;
          return `${hk_opt?.en_name} (+${hk_opt?.dial_code}) `;
        }
      }

      const _country = other_id_country || id_country;
      if (_country) {
        const item = allCountryList.filter((d) => d.iso_code2 === _country)[0];
        if (item) {
          if (_lang === "zh") return `${item?.zh_name} (+${item?.dial_code}) `;
          return `${item?.en_name} (+${item?.dial_code}) `;
        }
      }
    }
  }, [
    allCountryList,
    _lang,
    id_country,
    other_id_country,
    id_type,
    memoCountryList,
  ]);

  useEffect(() => {
    if (id_type === "ID_CARD" && memoCountryList?.length > 0) {
      const hk_opt = memoCountryList?.filter((d) => d.iso_code2 === "HK")[0];
      inputOnChange(hk_opt.iso_code2, "id_country");
      inputOnChange("", "has_other_country_id_card");
      inputOnChange("", other_id_country);
      inputOnChange([], "other_id_front_files");
      inputOnChange([], "other_id_opposite_files");
      inputOnChange("", "other_id_type");
      setAreaCompliantError(false);
      setAreaScanError(false);
    }
  }, [
    id_type,
    memoCountryList,
    other_id_country,
    inputOnChange,
    setAreaCompliantError,
    setAreaScanError,
  ]);

  useEffect(() => {
    getUserInfo();
  }, []);

  // 证件发行地国家
  const selectCountry = (value) => {
    inputOnChange(value.iso_code2, "id_country");
    setAreaScanError(false);
    setAreaCompliantError(false);
    // 在制裁区 + 美国、美国本土外小岛
    if (
      sanctionsCountryList.indexOf(value.iso_code2) > -1 ||
      value.iso_code2 === "US" ||
      value.iso_code2 === "UM"
    ) {
      setAreaScanError(true);
      return;
    }

    // 非合规区
    if (noCompliantCountryList.indexOf(value.iso_code2) > -1) {
      setAreaCompliantError(true);
      return;
    } else {
      // 合规地区
      inputOnChange("", "other_id_country");
    }
  };

  const renderItem = (item) => {
    return (
      <Flex alignItems={"center"} key={item.dial_code}>
        <Text fontSize="12px">
          {`${_lang === "zh" ? item.zh_name : item.en_name}  (+${
            item.dial_code
          })`}
        </Text>
      </Flex>
    );
  };

  const renderOtherCardType = (item) => {
    return (
      <Flex alignItems={"center"}>
        <Text fontSize="12px">{item.label}</Text>
      </Flex>
    );
  };

  // 正反面
  const renderOtherCountryCardFile = (type) => {
    const file_type = type === 1 ? "front" : "opposite";

    const file_value =
      file_type === "front"
        ? other_id_front_files?.[0]?.document_name ||
          other_id_front_files?.[0]?.document_path
        : other_id_opposite_files?.[0]?.document_name ||
          other_id_opposite_files?.[0]?.document_path;

    const file_error =
      file_type === "front"
        ? other_id_front_files?.[0]?.document_error
        : other_id_opposite_files?.[0]?.document_error;

    return (
      <>
        <Text mt={"30px"} color={"rgba(1, 3, 19, 1)"} fontSize={"14px"}>
          {file_type === "front"
            ? t("oSDxsXgENbIeNMLv")
            : t("aoAIqdHabiquSjWWz")}
        </Text>
        <BXUpload
          fileName={file_value}
          error={file_error}
          handleUploadChange={(e) => {
            handleUploadOtherCountryIdCardFile(e, file_type);
          }}
          placeholder={
            file_type === "front"
              ? t("idqLvJqjeiEmQbpa")
              : t("FhkGAkzeRaGMUGCREr")
          }
        ></BXUpload>
      </>
    );
  };

  return (
    <PageWrap>
      {pageStatus === "RESULT" && <Result />}
      {pageStatus === "INIT" && (
        <Box w={["80%", "400px"]} margin={"0 auto"} mt={"40px"}>
          {uploadLoading && <BXLoading />}
          <Center>
            <Image src="../../wwwsite/images/kyc/user.svg" />
            <Text
              fontSize={32}
              fontWeight={700}
              ml={"10px"}
              lineHeight={"36px"}
            >
              {t("IzkuvXVHteJlPPUrez")}
            </Text>
          </Center>
          {/* 基本信息 */}
          <Box mt={"50px"} paddingBottom={"20px"}>
            {/* 证件类型 */}
            <Flex mt={"30px"} flexDirection={"column"}>
              <Text fontSize="14px" fontWeight={600}>
                {t("VALtZMIyzODbkpPUOsxG")}
              </Text>
              <Flex
                mt={"10px"}
                display={"flex"}
                flexWrap={"wrap"}
                gap={"15px 40px"}
              >
                {memoCardType.map((d, i) => {
                  return (
                    <Flex key={i}>
                      <Image
                        w={"18px"}
                        onClick={() => {
                          inputOnChange(d.value, "id_type");
                          if (d.value === "ID_CARD") {
                            inputOnChange("", "other_id_country");
                          }
                        }}
                        cursor={"pointer"}
                        src={`../../wwwsite/images/kyc/radio${
                          inputParams.id_type === d.value ? "_checked" : ""
                        }.svg`}
                      />
                      <Text
                        fontSize={"14px"}
                        color={"t.100"}
                        ml={"4px"}
                        whiteSpace={"nowrap"}
                      >
                        {d.label}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>
            {/* 证件号码 */}
            <Text color={"#010313"} fontSize={"14px"} mt={"20px"}>
              {t("IzXQAIEgQLMYmZwrzP")}
            </Text>
            <BXInput
              maxLength={100}
              value={inputParams.id_number}
              onChange={(e) => inputOnChange(e.target.value, "id_number")}
              placeholder={t("idqLvJqjeiEmQbpa")}
            />
            {/* 证件有效期 */}
            {inputParams?.id_type === "PASSPORT" && (
              <Box>
                <Text fontSize="14px" mt="20px">
                  {t("DQmRuDUldvUfeEPb")}
                </Text>
                <DatePicker
                  style={{
                    width: "100%",
                    color: "#B3B4C8",
                    height: "44px",
                    marginTop: "10px",
                    fontSize: "10px",
                    borderRadius: "12px",
                    border: "1px solid #E3E4F1",
                    backgroundColor: "#fff",
                  }}
                  className="personal-key-date-pick"
                  placeholder={t("idqLvJqjeiEmQbpa")}
                  onChange={(date, dateString) => {
                    inputOnChange(dateString, "id_expiry_date");
                  }}
                  value={
                    inputParams?.id_expiry_date &&
                    dayjs(inputParams?.id_expiry_date, "YYYY-MM-DD")
                  }
                  locale={_lang === "en" ? "" : ZHlocale}
                />
                {inputParams?.id_expiry_date &&
                  !isLaterThanNowByAtLeast3Months(
                    inputParams?.id_expiry_date,
                  ) && (
                    <Text
                      fontSize="14px"
                      lineHeight="16px"
                      mt="10px"
                      color="#EA4136"
                    >
                      {t("uNPlYoEqFAqudrBt")}
                    </Text>
                  )}
              </Box>
            )}
            {/* 证件发行地 */}
            <Text color={"#010313"} fontSize={"14px"} mt={"20px"}>
              {t("xjuVMsJZTRgjPgAzSV")}
            </Text>
            <Box mt={"15px"}>
              <BXNSelect
                disabled={inputParams.id_type === "ID_CARD" ? true : false}
                options={memoCountryList}
                defaultOpt={defaultCountry}
                renderLabel={(item) => renderItem(item)}
                renderItem={(item) => renderItem(item)}
                handleSelect={(item) => {
                  selectCountry(item);
                }}
                renderPlaceholder={() => {
                  return (
                    <Text color="t.80" fontSize="14px">
                      {t("idqLvJqjeiEmQbpa")}
                    </Text>
                  );
                }}
              ></BXNSelect>
              {areaScanError && (
                <Text color={"#D04457"} fontSize={"14px"} mt={"5px"}>
                  {t("mzWsFtJNWfexuuUgYctv")}
                </Text>
              )}
            </Box>
            {showOtherCountryBlock && (
              <Box
                bg={"rgba(250, 250, 250, 1)"}
                borderRadius={"12px"}
                border={"1px solid rgba(227, 228, 241, 1)"}
                padding={"20px 15px"}
                mt={"10px"}
              >
                <Flex flexDirection={"column"}>
                  <Text fontWeight={400} fontSize={"14px"}>
                    {t("zLnEBdSfUXdqbRqAohY")}
                  </Text>
                  <Flex
                    mt={"10px"}
                    display={"flex"}
                    flexWrap={"wrap"}
                    gap={"15px 40px"}
                  >
                    {memoHasOtherCountryCard.map((d, i) => {
                      return (
                        <Flex ml={i === 1 && "40px"} key={i}>
                          <Image
                            w={"18px"}
                            onClick={() => {
                              inputOnChange(d, "has_other_country_id_card");
                              setOtherCountryAreaError(false);
                              inputOnChange("", "other_id_country");
                            }}
                            cursor={"pointer"}
                            src={`../../wwwsite/images/kyc/radio${
                              Number(has_other_country_id_card?.value) ===
                              Number(d.value)
                                ? "_checked"
                                : ""
                            }.svg`}
                          />
                          <Text
                            fontSize={"14px"}
                            color={"t.100"}
                            ml={"4px"}
                            whiteSpace={"nowrap"}
                          >
                            {d.label}
                          </Text>
                        </Flex>
                      );
                    })}
                  </Flex>
                  {Number(has_other_country_id_card?.value) === 2 && (
                    <Text color={"#D04457"} fontSize={"14px"} mt={"15px"}>
                      {t("mzWsFtJNWfexuuUgYctv")}
                    </Text>
                  )}
                </Flex>
                {Number(has_other_country_id_card?.value) === 1 && (
                  <Flex mt={"20px"} flexDirection={"column"}>
                    <Text fontSize={"14px"} fontWeight={400}>
                      {t("oVvhpgfhTeyLLnTSv")}
                    </Text>
                    <Text
                      fontSize="12px"
                      fontWeight={400}
                      mt={"10px"}
                      color={"rgba(0, 0, 0, 0.49)"}
                    >
                      {t("OEfurFwwcJaTsVxNdol")}
                    </Text>
                    <Flex mt={"30px"} flexDirection={"column"}>
                      <Text fontSize="14px" fontWeight={400}>
                        {t("xjuVMsJZTRgjPgAzSV")}
                      </Text>
                      <Box mt={"15px"}>
                        <BXNSelect
                          style={{ background: "#fff", borderRadius: "12px" }}
                          options={memoCountryList}
                          defaultOpt={defaultOtherCountry}
                          renderLabel={(item) => renderItem(item)}
                          renderItem={(item) => renderItem(item)}
                          handleSelect={(item) => {
                            selectOtherCountry(item);
                          }}
                          renderPlaceholder={() => {
                            return (
                              <Text color="t.80" fontSize="14px">
                                {t("idqLvJqjeiEmQbpa")}
                              </Text>
                            );
                          }}
                        ></BXNSelect>
                        {otherCountryAreaError && (
                          <Text color={"#D04457"} fontSize={"14px"} mt={"15px"}>
                            {t("mzWsFtJNWfexuuUgYctv")}
                          </Text>
                        )}
                      </Box>
                    </Flex>
                  </Flex>
                )}

                {!otherCountryAreaError &&
                  Number(has_other_country_id_card?.value) === 1 &&
                  inputParams?.other_id_country && (
                    <>
                      <Flex flexDirection={"column"} mt={"40px"}>
                        <Text fontSize="14px" fontWeight={400}>
                          {t("hoTQEpJFfRRLZGLWiDK")}
                        </Text>
                        <Box mt={"15px"}>
                          {/* 其他国家证件类型 */}
                          <BXNSelect
                            style={{ background: "#fff", borderRadius: "12px" }}
                            options={memoOtherCountryCardType}
                            defaultOpt={defaultOtherCountryCardType}
                            renderLabel={(item) => renderOtherCardType(item)}
                            renderItem={(item) => renderOtherCardType(item)}
                            handleSelect={(item) => {
                              inputOnChange(item.value, "other_id_type");
                              // selectOtherCountry(item);
                              // selectOtherCardType(it)
                            }}
                            renderPlaceholder={() => {
                              return (
                                <Text color="t.80" fontSize="14px">
                                  {t("idqLvJqjeiEmQbpa")}
                                </Text>
                              );
                            }}
                          ></BXNSelect>
                        </Box>
                      </Flex>
                      <Text
                        mt={"30px"}
                        color={"rgba(1, 3, 19, 1)"}
                        fontSize={"14px"}
                      >
                        {t("wZuMVSBUSlQidCQpjoS")}
                      </Text>
                      {renderOtherCountryCardFile(1)}
                      {renderOtherCountryCardFile(2)}
                      {/* 其他国家/地区证件有效期 */}
                      {inputParams?.id_type === "PASSPORT" && (
                        <Box>
                          <Text fontSize="14px" mt="20px">
                            {t("pnSuFkiSKSaSELfY")}
                          </Text>
                          <DatePicker
                            style={{
                              width: "100%",
                              color: "#B3B4C8",
                              height: "44px",
                              marginTop: "10px",
                              fontSize: "10px",
                              borderRadius: "12px",
                              border: "1px solid #E3E4F1",
                              backgroundColor: "#fff",
                            }}
                            className="personal-key-date-pick"
                            placeholder={t("idqLvJqjeiEmQbpa")}
                            onChange={(date, dateString) => {
                              inputOnChange(dateString, "other_id_expiry_date");
                            }}
                            value={
                              inputParams?.other_id_expiry_date &&
                              dayjs(
                                inputParams?.other_id_expiry_date,
                                "YYYY-MM-DD",
                              )
                            }
                            locale={_lang === "en" ? "" : ZHlocale}
                          />
                          {inputParams?.other_id_expiry_date &&
                            !isLaterThanNowByAtLeast3Months(
                              inputParams?.other_id_expiry_date,
                            ) && (
                              <Text
                                fontSize="14px"
                                lineHeight="16px"
                                mt="10px"
                                color="#EA4136"
                              >
                                {t("lLZTreJUqkRzBHsiZ")}
                              </Text>
                            )}
                        </Box>
                      )}
                    </>
                  )}
              </Box>
            )}
            {/* 更新证件信息 */}
            {/* {id_copy_files?.[0]?.document_path && ( */}
            <Box mt={"30px"}>
              <Text fontSize={"16px"} color={"#010313"} fontWeight={700}>
                {t("yuGCyrfFcdMpXvkMH")}
              </Text>
              <Text fontSize={"14px"} color={"#010313"} mt={"15px"}>
                {
                  memoCardType?.filter(
                    (d) => d.value === inputParams?.id_type,
                  )[0]?.label
                }
              </Text>

              <BXUpload
                style={{ marginTop: "15px" }}
                handleUploadChange={(e) => {
                  handleUoloadEsFile({
                    e: e,
                    type: "ID_COPY_FILE",
                  });
                }}
                fileName={id_copy_files?.[0]?.document_name}
                placeholder={t("idqLvJqjeiEmQbpa")}
                error={id_copy_files?.[0]?.document_error}
              />
            </Box>
            {/* )} */}

            {/* 我的常居地址也有变更 */}
            <Box
              borderBottom="1px solid rgba(227, 228, 241, 1)"
              width="100%"
              h="0px"
              mt="20px"
              mb="20px"
            ></Box>
            <Flex>
              <Image
                w={"18px"}
                src={`../../wwwsite/images/kyc/${needUpdateAddress ? "checked" : "no_check"}.svg`}
                cursor={"pointer"}
                onClick={() => {
                  setNeedUpdateAddress(!needUpdateAddress);
                }}
              />
              <Text
                fontSize={"14px"}
                color={"t.100"}
                ml={"4px"}
                whiteSpace={"nowrap"}
              >
                {t("hRjeaGhLtpLtTaKiR")}
              </Text>
            </Flex>
            {needUpdateAddress && (
              <Flex flexDirection={"column"}>
                {/* 常居地 */}
                <Text fontSize="14px" color="t.100" mt="20px">
                  {t("gYpQegnebgwwccEuM")}
                </Text>
                <BXInput
                  maxLength={100}
                  disabled
                  value={memoResidenceCountry}
                  backgroundColor={"rgba(32, 26, 85, 0.05)"}
                />
                {/* 常居地址 */}
                <Text color={"#010313"} fontSize={"14px"} mt={"20px"}>
                  {t("HQSjnUOWYQPuvvTpu")}
                </Text>
                <BXInput
                  maxLength={100}
                  value={inputParams.residence_address}
                  onChange={(e) =>
                    inputOnChange(e.target.value, "residence_address")
                  }
                  placeholder={t("idqLvJqjeiEmQbpa")}
                />
                {/* 地址证明 */}
                <Box mt={"30px"}>
                  <Text color={"#010313"} fontSize={"14px"} mt={"20px"}>
                    {t("GJOdwcCnvAbhzEkImJ")}
                  </Text>
                  <BXUpload
                    style={{ marginTop: "15px" }}
                    handleUploadChange={(e) => {
                      handleUoloadEsFile({
                        e: e,
                        type: "RESIDENCE_ADDRESS_FILE",
                      });
                    }}
                    fileName={clone_address_files?.[0]?.document_name}
                    placeholder={t("idqLvJqjeiEmQbpa")}
                    error={clone_address_files?.[0]?.document_error}
                    errorText={t("dLTuZclgDrLDUToEe")}
                  />
                </Box>
              </Flex>
            )}
          </Box>

          <Flex justifyContent={"center"} mt={"50px"} pb={"30px"}>
            <BXButton
              margin={"0 auto"}
              w={["80%", "250px"]}
              onClick={() => {
                if (sumitPreCheck()) {
                  setShowTipModal(true);
                }
              }}
            >
              {t("YChcfaxDaLscdSPiRQz")}
            </BXButton>
          </Flex>

          <ConfirmModal
            show={showTipModal}
            onOk={() => {
              onSubmit();
            }}
            onCancel={() => {
              setShowTipModal(false);
            }}
          />
        </Box>
      )}
    </PageWrap>
  );
};

export default UpdateKycInfo;
